import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) localStorage.setItem("beatflow_ref", ref.toUpperCase());
    // Returning from Google sign-in: AuthCallback exchanges the session first.
    if (window.location.hash?.includes("session_id=")) return;
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(false));
  }, []);

  const login = useCallback(async (email, password) => {
    await api.post("/auth/login", { email, password });
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const ref = localStorage.getItem("beatflow_ref") || undefined;
    await api.post("/auth/register", { name, email, password, ref_code: ref });
    localStorage.removeItem("beatflow_ref");
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  const googleLogin = useCallback(async (sessionId) => {
    const ref = localStorage.getItem("beatflow_ref") || undefined;
    await api.post("/auth/google", { session_id: sessionId, ref_code: ref });
    localStorage.removeItem("beatflow_ref");
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    setUser(false);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      setUser(false);
      return false;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, register, googleLogin, logout, setUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export { formatApiErrorDetail };
